import React from 'react';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { Skeleton } from 'antd';
import { ArrowLeftIcon } from 'icons';
import { GetStaticProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

import { extractImageSrc, globalConst, isEmptyArr, stripHtmlTags, useBack } from 'modules/global';
import {
  ArticleCards,
  articleCons,
  ArticleItemCard,
  articleSchemaJson,
  getNewsBySlug,
  getRecommendedNews,
  useNewsBySlug,
  useRecommendedNews
} from 'modules/news';

import { EmptyData, NextHead, Visible } from 'components';

declare global {
  type TRouterType = ReturnType<typeof useRouter> & {
    query: {
      name: string;
    };
  };
}

const Index: React.FC = () => {
  const t = useTranslations();
  const { query, asPath } = useRouter();
  const onBack = useBack(globalConst.pageNames.NEWS);

  const { data, isLoading } = useNewsBySlug(query?.name as string);
  const { data: suggestNews, isLoading: isSuggestNewsLoading } = useRecommendedNews(query?.name as string);

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              articleSchemaJson(
                {
                  ...data!,
                  image: extractImageSrc(data?.description) || ''
                },
                asPath
              )
            )
          }}
        />
      </Head>
      <NextHead title={data?.name} description={stripHtmlTags(data?.short_description).slice(0, 155)} />

      <div className="mx-auto my-7 min-h-screen max-w-[890px] p-4">
        <div onClick={onBack} className="inline-flex cursor-pointer items-center justify-start gap-2 font-semibold">
          <ArrowLeftIcon /> {t('common.ortga_qaytish')}
        </div>
        <div className="my-5">
          <Visible
            if={isLoading}
            else={
              <Visible if={data?.article_id} else={<EmptyData title={t('malumot_topilmadi')} />}>
                <ArticleItemCard
                  image={extractImageSrc(data?.description)}
                  title={data?.name}
                  desc={data?.description}
                  categoryTitle={data?.category_name}
                  viewCount={data?.views_count}
                  createdAt={data?.created_time}
                />
              </Visible>
            }
          >
            <div className="space-y-4 rounded-xl bg-white p-4">
              <Skeleton.Button active className="!w-full" />

              <div className="item-center flex justify-between">
                <div>
                  <Skeleton.Button active className="!h-4" />
                </div>
                <div className="space-x-3">
                  <Skeleton.Button active className="!w-30 !h-4" />
                  <Skeleton.Button active className="!w-30 !h-4" />
                </div>
              </div>

              <div className="h-110 relative w-full flex-shrink-0">
                <Skeleton.Image active className="!h-full !w-full" />
              </div>

              <div className="space-y-5">
                <Skeleton active />
                <Skeleton active />
              </div>
            </div>
          </Visible>
        </div>

        {data?.article_id && (
          <>
            <div className="font-medium">{t('tavsiya_etamiz')}</div>
            <div className="mt-5 w-full sm:col-span-3">
              <Visible
                if={isSuggestNewsLoading}
                else={
                  <Visible
                    if={isEmptyArr(suggestNews?.data)}
                    else={suggestNews?.data?.map(article => (
                      <ArticleCards
                        key={article.article_id}
                        title={article.name}
                        slug={article?.slug}
                        desc={article.description}
                        categoryTitle={article.category_name}
                        viewCount={article.views_count}
                        createdAt={article.created_time}
                      />
                    ))}
                  >
                    <EmptyData title={t('malumot_topilmadi')} />
                  </Visible>
                }
              >
                {Array.from({ length: 5 }).map((_, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={index} className="mb-4 flex flex-col gap-5 rounded-xl bg-white p-3 sm:flex-row">
                    <div className="sm:w-50 sm:min-w-50 relative h-40 w-full flex-shrink-0 md:min-w-60">
                      <Skeleton.Image className="!h-full !w-full" active />
                    </div>
                    <div className="flex-1">
                      <div>
                        <div>
                          <Skeleton.Input className="!w-full" active />
                        </div>
                        <div className=" mt-6 flex flex-col space-y-4">
                          <Skeleton.Input className="!h-3 !w-full" active />
                          <Skeleton.Input className="!w-100 !h-3" active />
                          <Skeleton.Input className="!w-70 !h-3" active />
                          <Skeleton.Input className="!h-3 !w-full" active />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </Visible>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Index;

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  const queryClient = new QueryClient();

  await Promise.allSettled([
    queryClient.prefetchQuery({
      queryKey: [articleCons.queryKeys.ARTICLE_BY_SLUG, params?.name as string],
      queryFn: getNewsBySlug(params?.name as string)
    }),
    queryClient.prefetchQuery({
      queryKey: [articleCons.queryKeys.RECOMMENDED_ARTICLES, params?.name as string],
      queryFn: getRecommendedNews(params?.name as string)
    })
  ]);

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      messages: (await import(`../../../messages/${locale}.json`)).default
    },
    revalidate: 604800
  };
};

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking'
  };
}
